import { memo, useCallback, useState, useEffect } from 'react';
import Layout from '../../../../components/layout';
import PageHeading from '../../../../components/common/page-heading';
import { User, Customer } from '../../../../fsm/users';

import { Button, Space, Typography, Table, Tabs, Popconfirm } from 'antd';
import type { TablePaginationConfig } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  ReloadOutlined,
  ExclamationCircleFilled,
  PlusOutlined,
} from '@ant-design/icons';

import AddUserModal from '../../components/add-user-modal';
import RoleTag from '../../components/role-tag';
import PermissionOverrideBadge from '../../components/permission-override-badge';
import RolePermissionsCard from '../../components/role-permissions-card';
import { semantic } from '../../../../styles/tokens';
import styles from './index.module.css';
import { UserRole } from '../../utils/role';
import { DISPLAYED_ROLES } from '../../utils/roleCapabilities';
import { usePermissions } from '../../../../context/permission.context';

const { Title, Text } = Typography;

interface Props {
  user?: User;
  data: User[];
  customers: Customer[];
  isLoading: boolean;
  totalElements: number;
  onFetchUsers: (pageNumber: number) => void;
  onFetchCustomers: () => void;
  onDeleteUser: (id: number) => void;
  onResetPassword: (email: string) => void;
  onUpdateUser: (data: Partial<User>, id: number) => void;
  onAddUser: (
    firstName: string,
    lastName: string,
    email: string,
    role: string,
    customerName?: number,
  ) => void;
}

const Users = ({
  user,
  data,
  customers,
  isLoading,
  totalElements,
  onFetchUsers,
  onFetchCustomers,
  onAddUser,
  onUpdateUser,
  onDeleteUser,
  onResetPassword,
}: Props) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState<'users' | 'roles'>('users');
  const {
    canAddNewUser,
    canSeeUserActionsColumn,
    canSeeUserCustomerColumn,
    canSeeAddUserCustomerField,
    canCustomizeUserPermissions,
  } = usePermissions();

  useEffect(() => {
    if (user) {
      onFetchUsers(1);
    }
  }, [onFetchUsers, user]);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setEditUser(null);
  }, [setIsModalOpen]);

  const handleModalOpen = useCallback(
    (user?: User) => () => {
      if (user) {
        setEditUser(user);
      }
      setIsModalOpen(true);
    },
    [setIsModalOpen, setEditUser],
  );

  const handleTableChange = useCallback(
    (pagination: TablePaginationConfig) => {
      onFetchUsers(pagination.current as number);
    },
    [onFetchUsers],
  );

  const confirmDelete = useCallback(
    (id: number) => () => {
      onDeleteUser(id);
    },
    [onDeleteUser],
  );

  const confirmResetPassword = useCallback(
    (email: string) => () => {
      onResetPassword(email);
    },
    [onResetPassword],
  );

  const columns = [
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => <RoleTag role={role} />,
    },
    ...(canCustomizeUserPermissions
      ? [
          {
            title: 'Permissions',
            dataIndex: 'overrideCount',
            key: 'overrideCount',
            render: (overrideCount: number | undefined, item: User) => (
              <PermissionOverrideBadge
                userId={item.id}
                overrideCount={overrideCount}
              />
            ),
          },
        ]
      : []),
    {
      title: 'Full name',
      dataIndex: 'firstName',
      key: 'firstName',
      render: (firstName: string, item: User) => (
        <Typography>
          {firstName} {item.lastName}
        </Typography>
      ),
    },
    ...(canSeeUserCustomerColumn
      ? [
          {
            title: 'Customer name',
            dataIndex: 'customerName',
            key: 'customerName',
          },
        ]
      : []),
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Last active',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      render: (lastLoginAt?: number) => (
        <>
          {lastLoginAt
            ? new Intl.DateTimeFormat('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
              }).format(new Date(lastLoginAt * 1000))
            : 'No date'}
        </>
      ),
    },
    ...(canSeeUserActionsColumn
      ? [
          {
            title: 'Actions',
            dataIndex: 'listing',
            key: 'listing',
            render: (_e: string, user: User) => (
              <Space size={4} className={styles.actions}>
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={handleModalOpen(user)}
                ></Button>
                <Popconfirm
                  title="Are you sure you want to reset password?"
                  description="An email with reset instructions will be sent to the user's email."
                  icon={
                    <ExclamationCircleFilled
                      style={{ color: semantic.warning }}
                    />
                  }
                  onConfirm={confirmResetPassword(user.email)}
                  okText="Reset"
                  cancelText="No"
                >
                  <Button type="text" icon={<ReloadOutlined />}></Button>
                </Popconfirm>
                <Popconfirm
                  title="Are you sure you want to delete a user?"
                  description="They will no longer have access to the platform."
                  icon={
                    <ExclamationCircleFilled
                      style={{ color: semantic.error }}
                    />
                  }
                  onConfirm={confirmDelete(user.id)}
                  okText="Delete"
                  cancelText="No"
                >
                  <Button
                    type="text"
                    icon={
                      <Text type="danger">
                        <DeleteOutlined />
                      </Text>
                    }
                  ></Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <Layout user={user}>
      <div className={styles.header}>
        <Space direction="vertical" size={4}>
          <Title level={1} className={styles.title}>
            Profile management
          </Title>
          <Text type="secondary" className={styles.subtitle}>
            Add and manage user profiles for hiring managers and recruiters.
          </Text>
        </Space>
        {canAddNewUser && activeTab === 'users' && (
          <Button
            type="primary"
            shape="round"
            icon={<PlusOutlined />}
            onClick={handleModalOpen()}
          >
            Add new user
          </Button>
        )}
      </div>
      <div className={styles.wrapper}>
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as 'users' | 'roles')}
          items={[
            {
              key: 'users',
              label: 'Users',
              children: (
                <>
                  <PageHeading
                    title="Users"
                    level="section"
                    count={{
                      value: totalElements,
                      singular: 'user',
                      plural: 'users',
                    }}
                  />
                  <Table
                    dataSource={data}
                    columns={columns}
                    loading={isLoading}
                    rowClassName={styles.row}
                    scroll={{ x: 800 }}
                    pagination={{
                      total: totalElements,
                      pageSize: 10,
                      showSizeChanger: false,
                    }}
                    onChange={handleTableChange}
                  />
                </>
              ),
            },
            {
              key: 'roles',
              label: 'Roles & permissions',
              children: (
                <div className={styles.roleCards}>
                  {DISPLAYED_ROLES.map((role) => (
                    <RolePermissionsCard
                      key={role}
                      role={role}
                      assignable={role !== UserRole.SUPER_ADMIN}
                    />
                  ))}
                </div>
              ),
            },
          ]}
        />
      </div>
      <AddUserModal
        isOpen={isModalOpen}
        isLoading={isLoading}
        customers={customers}
        user={editUser}
        canSeeAddUserCustomerField={canSeeAddUserCustomerField}
        canCustomizeUserPermissions={canCustomizeUserPermissions}
        onClose={handleModalClose}
        onFetchCustomers={onFetchCustomers}
        onSubmit={onAddUser}
        onUpdateUser={onUpdateUser}
      />
    </Layout>
  );
};

export default memo(Users);
